/**
 * Annotation mode — full video telestration markup. React migration Pass 11,
 * ported from app/templates/game_match_summary/index.html:
 *   - annoState + enter/exit annotation mode (~8903-8985)
 *   - drawing event handlers incl. pitch mode (~9103-9293)
 *   - save/load/undo/clear against /game-match-summary/api/annotations (~9294-9412)
 *   - drawAnnotationOverlay / drawAnnotationShape (~9380-9589)
 *   - the annoLoop rAF hook, captureScreenshot, seekToAnnotation (~9560-9617)
 *   - the rewired Telestrate button behavior (~9618)
 *
 * The toolbar itself is a React component (AnnotationToolbar.tsx) — the
 * template swapped the moments panel's innerHTML for a toolbar string and
 * stashed/restored the old HTML; here GameMatchSummary swaps components on
 * onAnnotationModeChange instead, so savedMomentsHTML has no equivalent.
 */
import { frameToVideoTime, videoTimeToFrame } from './frameTime'
import { pixelToWorld, worldToPixel } from './telestration'
import type { Homography } from './telestration'
import type { CanvasController } from './canvasController'

export type AnnoTool = 'select' | 'freehand' | 'arrow' | 'circle' | 'text' | 'player_connect'

/** A stored coordinate pair, checked.
 *
 *  Annotations round-trip through the server as JSON, so the arrays below are
 *  genuinely `number[]` — nothing guarantees a "pair" has two numbers in it.
 *  Every drawing path asks this rather than indexing and hoping, so a
 *  malformed shape is skipped instead of drawn at (NaN, NaN). */
function pair(pt: number[] | undefined): [number, number] | null {
  const [a, b] = pt ?? []
  return a === undefined || b === undefined ? null : [a, b]
}

/** moveTo the first point, lineTo the rest. Four shapes here walked an array
 *  with `screen[i]`, which reads as a possibly-missing lookup. */
function strokePolyline(
  ctx: CanvasRenderingContext2D,
  points: readonly { x: number; y: number }[],
  close = false,
): void {
  const [first, ...rest] = points
  if (first === undefined) return
  ctx.beginPath()
  ctx.moveTo(first.x, first.y)
  for (const p of rest) ctx.lineTo(p.x, p.y)
  if (close) ctx.closePath()
  ctx.stroke()
}

export interface AnnotationShapeData {
  coord_space?: 'pitch' | 'screen'
  points?: number[][]
  start?: number[]
  end?: number[]
  center?: number[]
  radius?: number
  position?: number[]
  text?: string
  fontSize?: number
}

export interface AnnotationRecord {
  id: number
  user_id?: number
  frame: number
  frame_start?: number
  frame_end?: number
  annotation_type: string
  data: AnnotationShapeData
  color: string
  stroke_width: number
  title?: string
}

interface CurrentDrawing extends AnnotationShapeData {
  type: string
  color: string
  strokeWidth: number
  frame?: number
}

export const ANNO_COLORS = ['#ff0000', '#3b82f6', '#ffffff', '#facc15', '#22c55e', '#000000']

export class AnnotationController {
  private controller: CanvasController

  annoCanvas: HTMLCanvasElement | null = null
  annoCtx: CanvasRenderingContext2D | null = null

  // Annotation state (template annoState)
  active = false
  tool: AnnoTool = 'select'
  color = '#ff0000'
  strokeWidth = 3
  pitchMode = false // draw tools anchor to pitch (world meters) instead of screen pixels
  annotations: AnnotationRecord[] = []
  undoStack: number[] = []
  currentDrawing: CurrentDrawing | null = null
  arrowStart: { x: number; y: number } | null = null
  private arrowStartWorld: number[] | null = null

  /** Toolbar re-render hook (React mirrors tool/color/width/pitchMode). */
  onToolStateChange: (() => void) | null = null

  private rafId: number | null = null
  private listeners: { type: string; fn: (e: MouseEvent) => void }[] = []

  constructor(controller: CanvasController) {
    this.controller = controller
  }

  /** The controller's video, popped out or not (see videoEl). */
  private get video(): HTMLVideoElement | null {
    return this.controller.videoEl()
  }

  init(): void {
    this.annoCanvas = document.getElementById('annotation-canvas') as HTMLCanvasElement | null
    this.annoCtx = this.annoCanvas ? this.annoCanvas.getContext('2d') : null
    this.initDrawing()
    this.startAnnoLoop()

    // Template loadAnnoOnInit: fetch annotations once the game is known so
    // annotation cards appear in the moments grid. gameCode is fixed at
    // construction here, so no 500ms readiness poll is needed.
    fetch(`/api/v1/game-match-summary/annotations/${this.controller.gameCode}`, {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.annotations = data.annotations || []
          this.controller.opts.onAnnotationsChange?.(this.annotations)
        }
      })
      .catch(() => {})
  }

  destroy(): void {
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.rafId = null
    if (this.annoCanvas) {
      for (const { type, fn } of this.listeners) {
        this.annoCanvas.removeEventListener(type, fn as EventListener)
      }
    }
    this.listeners = []
  }

  private notifyToolState(): void {
    this.onToolStateChange?.()
  }

  // -------------------------------------------------------------------------
  // Mode enter/exit
  // -------------------------------------------------------------------------

  enterAnnotationMode(): void {
    this.active = true
    const video = this.video
    if (video && !video.paused) video.pause()

    // Show annotation canvas
    if (this.annoCanvas) {
      this.annoCanvas.style.display = 'block'
      const vidW = video ? video.videoWidth || 1920 : 1920
      const vidH = video ? video.videoHeight || 1080 : 1080
      this.annoCanvas.width = vidW
      this.annoCanvas.height = vidH
    }

    // React swaps the moments panel for the toolbar (template stashed innerHTML)
    this.controller.opts.onAnnotationModeChange?.(true)

    // Load existing annotations
    this.loadAnnotations()

    // Preload homography so Pitch Mode is responsive on first toggle.
    if (this.controller.hasTelestration) this.controller.telestration.requestTelestrationData()

    // Telestration controls hide while annotation mode is active — handled by
    // the VideoPlayer's render condition off onAnnotationModeChange.
  }

  exitAnnotationMode(): void {
    this.active = false
    this.tool = 'select'
    this.currentDrawing = null
    this.arrowStart = null

    // Hide annotation canvas
    if (this.annoCanvas) {
      this.annoCanvas.style.display = 'none'
      this.annoCanvas.style.pointerEvents = 'none'
    }

    // React restores the moments panel (and re-renders annotation cards)
    this.controller.opts.onAnnotationModeChange?.(false)

    // Clear annotation canvas
    if (this.annoCtx && this.annoCanvas) {
      this.annoCtx.clearRect(0, 0, this.annoCanvas.width, this.annoCanvas.height)
    }
  }

  /**
   * The rewired Telestrate button (template rewireTelestrateButton): enters
   * full annotation mode with the player-connect video overlay enabled, or
   * exits if already active.
   */
  telestrateButtonClick(): void {
    if (this.active) {
      this.exitAnnotationMode()
    } else {
      // Enable player connect for video overlay AND enter annotation mode
      const tel = this.controller.telestration
      tel.telActiveLayers['connect'] = true
      if (tel.telCanvas) tel.telCanvas.style.display = 'block'
      tel.requestTelestrationData()
      this.enterAnnotationMode()
    }
  }

  // -------------------------------------------------------------------------
  // Tool state (template setAnnoTool / setAnnoColor / toggleAnnoPitchMode)
  // -------------------------------------------------------------------------

  setTool(tool: AnnoTool): void {
    this.tool = tool
    this.arrowStart = null
    this.currentDrawing = null

    // Player connect tool uses spatial canvas clicks, not annotation canvas
    const isDrawTool = !['select', 'player_connect'].includes(tool)
    if (this.annoCanvas) {
      this.annoCanvas.style.pointerEvents = isDrawTool ? 'auto' : 'none'
      this.annoCanvas.style.cursor = tool === 'text' ? 'text' : isDrawTool ? 'crosshair' : 'default'
    }

    // Toggle player connect layer for spatial → video connection
    const tel = this.controller.telestration
    tel.telActiveLayers['connect'] = tool === 'player_connect'
    if (tool === 'player_connect') {
      tel.selectedPlayers = []
      if (tel.telCanvas) tel.telCanvas.style.display = 'block'
      tel.requestTelestrationData()
    }

    this.notifyToolState()
  }

  setColor(color: string): void {
    this.color = color
    this.notifyToolState()
  }

  setStrokeWidth(width: number): void {
    this.strokeWidth = width
  }

  togglePitchMode(): void {
    this.pitchMode = !this.pitchMode
    if (this.pitchMode && this.controller.hasTelestration) {
      // Kick off a homography fetch so the first stroke is responsive.
      this.controller.telestration.requestTelestrationData()
    }
    this.notifyToolState()
  }

  // -------------------------------------------------------------------------
  // Drawing event handlers (template initAnnotationDrawing)
  // -------------------------------------------------------------------------

  private initDrawing(): void {
    const annoCanvas = this.annoCanvas
    if (!annoCanvas) return
    let isDrawing = false

    const getCanvasPos = (e: MouseEvent) => {
      const rect = annoCanvas.getBoundingClientRect()
      return {
        x: ((e.clientX - rect.left) / rect.width) * annoCanvas.width,
        y: ((e.clientY - rect.top) / rect.height) * annoCanvas.height,
      }
    }

    const getCurrentFrame = () => {
      const video = this.video
      if (!video) return 0
      return videoTimeToFrame(this.controller.getSync(), video.currentTime)
    }

    // When pitch mode is on, convert a pixel point to world meters using the
    // current frame's homography. Returns null if mode is off or no matrix.
    const maybeToWorld = (pos: { x: number; y: number }): [number, number] | null => {
      if (!this.pitchMode) return null
      const matrix = this.controller.telestration.getCurrentMatrix()
      if (!matrix) {
        console.warn('Pitch mode: homography not yet loaded for this frame')
        return null
      }
      const w = pixelToWorld(matrix, pos.x, pos.y)
      return w ? [w.x, w.y] : null
    }

    const onMouseDown = (e: MouseEvent) => {
      if (!this.active || this.tool === 'select') return
      const pos = getCanvasPos(e)
      const wpoint = maybeToWorld(pos)
      // If pitch mode is on but homography isn't ready, abort the stroke.
      if (this.pitchMode && !wpoint) return
      const pitch = !!wpoint

      if (this.tool === 'freehand') {
        isDrawing = true
        this.currentDrawing = {
          type: 'freehand',
          coord_space: pitch ? 'pitch' : 'screen',
          points: [pitch ? wpoint! : [pos.x, pos.y]],
          color: this.color,
          strokeWidth: this.strokeWidth,
          frame: getCurrentFrame(),
        }
      } else if (this.tool === 'circle') {
        isDrawing = true
        this.currentDrawing = {
          type: 'circle',
          coord_space: pitch ? 'pitch' : 'screen',
          center: pitch ? wpoint! : [pos.x, pos.y],
          radius: 0, // meters when pitch, pixels when screen
          color: this.color,
          strokeWidth: this.strokeWidth,
          frame: getCurrentFrame(),
        }
      } else if (this.tool === 'arrow') {
        if (!this.arrowStart) {
          // Stash both pixel pos (for hover preview) and world (for save)
          this.arrowStart = pos
          this.arrowStartWorld = wpoint
        } else {
          // Second click — complete arrow
          const startWorld = this.arrowStartWorld
          const endWorld = wpoint
          const completedInPitch = pitch && startWorld
          const anno: CurrentDrawing = {
            type: 'arrow',
            coord_space: completedInPitch ? 'pitch' : 'screen',
            start: completedInPitch ? startWorld! : [this.arrowStart.x, this.arrowStart.y],
            end: completedInPitch ? endWorld! : [pos.x, pos.y],
            color: this.color,
            strokeWidth: this.strokeWidth,
            frame: getCurrentFrame(),
          }
          this.arrowStart = null
          this.arrowStartWorld = null
          this.currentDrawing = null
          this.saveAnnotation(anno)
        }
      } else if (this.tool === 'text') {
        const text = prompt('Enter text:')
        if (text && text.trim()) {
          // Text stays screen-anchored — pitch-anchored text would need
          // perspective scaling to read well, deferred.
          const anno: CurrentDrawing = {
            type: 'text',
            coord_space: 'screen',
            position: [pos.x, pos.y],
            text: text.trim(),
            fontSize: 24,
            color: this.color,
            strokeWidth: this.strokeWidth,
            frame: getCurrentFrame(),
          }
          this.saveAnnotation(anno)
        }
      }
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!isDrawing || !this.currentDrawing) return
      const pos = getCanvasPos(e)
      const drawing = this.currentDrawing
      const isPitch = drawing.coord_space === 'pitch'

      if (drawing.type === 'freehand') {
        if (isPitch) {
          const w = maybeToWorld(pos)
          if (w) drawing.points!.push(w)
        } else {
          drawing.points!.push([pos.x, pos.y])
        }
      } else if (drawing.type === 'circle') {
        const center = pair(drawing.center)
        if (center) {
          if (isPitch) {
            const w = maybeToWorld(pos)
            if (w) {
              drawing.radius = Math.hypot(w[0] - center[0], w[1] - center[1]) // meters
            }
          } else {
            drawing.radius = Math.hypot(pos.x - center[0], pos.y - center[1]) // pixels
          }
        }
      }

      this.drawAnnotationOverlay()
    }

    const onMouseUp = (e: MouseEvent) => {
      if (!isDrawing || !this.currentDrawing) return
      isDrawing = false

      const drawing = this.currentDrawing
      this.currentDrawing = null

      // For circles, recompute radius at mouseup from the release position.
      // This is robust to fast clicks where mousemove never fires (or fires
      // before maybeToWorld can resolve the matrix).
      if (drawing.type === 'circle') {
        const pos = getCanvasPos(e)
        const isPitch = drawing.coord_space === 'pitch'
        const center = pair(drawing.center)
        if (isPitch) {
          const w = maybeToWorld(pos)
          if (w && center) {
            drawing.radius = Math.hypot(w[0] - center[0], w[1] - center[1])
          }
          // Click without a drag → default to a useful size on the pitch
          if (!drawing.radius || drawing.radius < 0.5) drawing.radius = 1.5 // meters
        } else {
          if (center) drawing.radius = Math.hypot(pos.x - center[0], pos.y - center[1])
          if (!drawing.radius || drawing.radius < 8) drawing.radius = 30 // pixels
        }
      }

      if (drawing.type === 'freehand' && drawing.points!.length > 2) {
        this.saveAnnotation(drawing)
      } else if (drawing.type === 'circle') {
        this.saveAnnotation(drawing)
      }
    }

    // Show arrow preview on hover after first click. Preview always renders
    // in screen space — it's just a hint, not a saved shape.
    const onArrowPreview = (e: MouseEvent) => {
      if (this.tool === 'arrow' && this.arrowStart) {
        const pos = getCanvasPos(e)
        this.currentDrawing = {
          type: 'arrow',
          coord_space: 'screen',
          start: [this.arrowStart.x, this.arrowStart.y],
          end: [pos.x, pos.y],
          color: this.color,
          strokeWidth: this.strokeWidth,
        }
        this.drawAnnotationOverlay()
      }
    }

    annoCanvas.addEventListener('mousedown', onMouseDown)
    annoCanvas.addEventListener('mousemove', onMouseMove)
    annoCanvas.addEventListener('mouseup', onMouseUp)
    annoCanvas.addEventListener('mousemove', onArrowPreview)
    this.listeners = [
      { type: 'mousedown', fn: onMouseDown },
      { type: 'mousemove', fn: onMouseMove },
      { type: 'mouseup', fn: onMouseUp },
      { type: 'mousemove', fn: onArrowPreview },
    ]
  }

  // -------------------------------------------------------------------------
  // Save / Load
  // -------------------------------------------------------------------------

  saveAnnotation(anno: CurrentDrawing): void {
    const frame = anno.frame || 0
    const shapeData: AnnotationShapeData = { coord_space: anno.coord_space || 'screen' }

    if (anno.type === 'freehand') shapeData.points = anno.points
    else if (anno.type === 'arrow') {
      shapeData.start = anno.start
      shapeData.end = anno.end
    } else if (anno.type === 'circle') {
      shapeData.center = anno.center
      shapeData.radius = anno.radius
    } else if (anno.type === 'text') {
      shapeData.position = anno.position
      shapeData.text = anno.text
      shapeData.fontSize = anno.fontSize
    }

    // Default visibility window per shape type. Text labels and pitch-anchored
    // markup are useful much longer than ephemeral screen-space drawings:
    //   - text:           ~30 sec (labels read across a play)
    //   - pitch shapes:   ~10 sec (tracks the camera, useful through a clip)
    //   - screen shapes:  ~2 sec (ephemeral, fades while the camera moves on)
    let visibilityFrames: number
    if (anno.type === 'text') visibilityFrames = 750
    else if (shapeData.coord_space === 'pitch') visibilityFrames = 250
    else visibilityFrames = 50

    const payload = {
      game_code: this.controller.gameCode,
      frame: frame,
      frame_start: frame,
      frame_end: frame + visibilityFrames,
      annotation_type: anno.type,
      data: shapeData,
      color: anno.color,
      stroke_width: anno.strokeWidth,
    }

    fetch('/api/v1/game-match-summary/annotations', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.annotations.push(data.annotation)
          this.undoStack.push(data.annotation.id)
          this.drawAnnotationOverlay()
          this.controller.opts.onAnnotationsChange?.(this.annotations)
        }
      })
      .catch((e) => console.error('Failed to save annotation:', e))
  }

  loadAnnotations(): void {
    fetch(`/api/v1/game-match-summary/annotations/${this.controller.gameCode}`, {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.annotations = data.annotations || []
          this.drawAnnotationOverlay()
          this.controller.opts.onAnnotationsChange?.(this.annotations)
        }
      })
      .catch((e) => console.warn('Failed to load annotations:', e))
  }

  undo(): void {
    const lastId = this.undoStack.pop()
    if (!lastId) return

    fetch(`/api/v1/game-match-summary/annotations/${lastId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      // The CSRF guard requires this on every cookie-authenticated write,
      // body or not.
      headers: { 'Content-Type': 'application/json' },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          this.annotations = this.annotations.filter((a) => a.id !== lastId)
          this.drawAnnotationOverlay()
          this.controller.opts.onAnnotationsChange?.(this.annotations)
        }
      })
      .catch((e) => console.error('Failed to undo annotation:', e))
  }

  clearAll(): void {
    if (!confirm('Clear all annotations for this game?')) return
    const promises = this.annotations
      .filter((a) => a.user_id === (this.controller.opts.currentUserId || 0))
      .map((a) =>
        fetch(`/api/v1/game-match-summary/annotations/${a.id}`, {
          method: 'DELETE',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
        }),
      )

    Promise.all(promises).then(() => {
      this.loadAnnotations()
      this.undoStack = []
    })
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  drawAnnotationOverlay(): void {
    if (!this.annoCtx || !this.annoCanvas) return
    this.annoCtx.clearRect(0, 0, this.annoCanvas.width, this.annoCanvas.height)

    const video = this.video
    const currentFrame = video ? videoTimeToFrame(this.controller.getSync(), video.currentTime) : 0

    // Look up once per frame; pitch-anchored shapes need it to reproject.
    const matrix = this.controller.telestration.getCurrentMatrix()

    // Draw saved annotations visible at current frame
    for (const anno of this.annotations) {
      const start = anno.frame_start || anno.frame
      const end = anno.frame_end || anno.frame + 50
      if (currentFrame < start || currentFrame > end) continue

      this.drawAnnotationShape(anno.annotation_type, anno.data, anno.color, anno.stroke_width, matrix)
    }

    // Draw in-progress shape
    if (this.currentDrawing) {
      this.drawAnnotationShape(
        this.currentDrawing.type,
        this.currentDrawing,
        this.currentDrawing.color,
        this.currentDrawing.strokeWidth,
        matrix,
      )
    }
  }

  drawAnnotationShape(
    type: string,
    data: AnnotationShapeData,
    color: string,
    strokeWidth: number,
    matrix: Homography | null,
  ): void {
    const annoCtx = this.annoCtx!
    annoCtx.strokeStyle = color
    annoCtx.fillStyle = color
    annoCtx.lineWidth = strokeWidth || 3
    annoCtx.lineCap = 'round'
    annoCtx.lineJoin = 'round'

    const isPitch = data.coord_space === 'pitch'
    // Pitch-anchored shapes can't render without a homography matrix; skip
    // entirely rather than draw at stale screen coords.
    if (isPitch && !matrix) return

    // Project a stored point (pixel pair OR world pair depending on
    // coord_space) to current screen pixel coords.
    const project = (pt: number[]): { x: number; y: number } | null => {
      const p = pair(pt)
      if (p === null) return null
      if (!isPitch) return { x: p[0], y: p[1] }
      // isPitch with no matrix returned above; repeating the check here is
      // what lets this drop the assertion the port arrived with.
      return matrix === null ? null : worldToPixel(matrix, p[0], p[1])
    }

    if (type === 'freehand') {
      const pts = data.points
      if (!pts || pts.length < 2) return
      const screen = pts.map(project).filter((p): p is { x: number; y: number } => p !== null)
      if (screen.length < 2) return
      strokePolyline(annoCtx, screen)
    } else if (type === 'arrow') {
      if (!data.start || !data.end) return
      const s = project(data.start)
      const e = project(data.end)
      if (!s || !e) return
      annoCtx.beginPath()
      annoCtx.moveTo(s.x, s.y)
      annoCtx.lineTo(e.x, e.y)
      annoCtx.stroke()
      // Arrowhead in screen space using the projected line direction
      const angle = Math.atan2(e.y - s.y, e.x - s.x)
      const headLen = 20
      annoCtx.beginPath()
      annoCtx.moveTo(e.x, e.y)
      annoCtx.lineTo(e.x - headLen * Math.cos(angle - 0.4), e.y - headLen * Math.sin(angle - 0.4))
      annoCtx.moveTo(e.x, e.y)
      annoCtx.lineTo(e.x - headLen * Math.cos(angle + 0.4), e.y - headLen * Math.sin(angle + 0.4))
      annoCtx.stroke()
    } else if (type === 'circle') {
      const centerPt = pair(data.center)
      if (!centerPt || !data.radius) return
      if (!isPitch) {
        // Screen-space circle — simple arc
        annoCtx.beginPath()
        annoCtx.arc(centerPt[0], centerPt[1], data.radius, 0, Math.PI * 2)
        annoCtx.stroke()
      } else {
        // Pitch-space circle. Project the center and 48 world points around
        // the circumference to handle perspective ellipsing. If most sample
        // points fall outside the homography's valid range, fall back to a
        // screen-space arc using a single projected radius offset — less
        // perspective-accurate but always renders.
        const [cx, cy] = centerPt
        const r = data.radius
        if (matrix === null) return
        const N = 48
        const screen: { x: number; y: number }[] = []
        for (let i = 0; i < N; i++) {
          const t = (i / N) * Math.PI * 2
          const wx = cx + r * Math.cos(t)
          const wy = cy + r * Math.sin(t)
          const p = worldToPixel(matrix, wx, wy)
          if (p) screen.push(p)
        }

        if (screen.length >= N * 0.6) {
          // Plenty of sample points — draw the projected polygon
          strokePolyline(annoCtx, screen, true)
        } else {
          // Fallback: project center + east point, draw screen-space arc
          const center = worldToPixel(matrix, cx, cy)
          const east = worldToPixel(matrix, cx + r, cy)
          if (!center || !east) return
          const pxR = Math.hypot(east.x - center.x, east.y - center.y)
          if (pxR < 1) return
          annoCtx.beginPath()
          annoCtx.arc(center.x, center.y, pxR, 0, Math.PI * 2)
          annoCtx.stroke()
        }
      }
    } else if (type === 'text') {
      const pos = pair(data.position)
      const text = data.text
      if (!pos || !text) return
      const fontSize = data.fontSize || 24
      annoCtx.font = `bold ${fontSize}px Arial`
      const textW = annoCtx.measureText(text).width
      // Background pill
      annoCtx.fillStyle = 'rgba(0, 0, 0, 0.7)'
      annoCtx.beginPath()
      annoCtx.roundRect(pos[0] - 6, pos[1] - fontSize + 2, textW + 12, fontSize + 8, 4)
      annoCtx.fill()
      // Text
      annoCtx.fillStyle = color
      annoCtx.textBaseline = 'top'
      annoCtx.fillText(text, pos[0], pos[1] - fontSize + 6)
    }
  }

  // Hook annotation rendering into a rAF loop (template hookAnnoLoop)
  private startAnnoLoop(): void {
    const video = this.video
    if (!video) return
    let lastAnnoFrame = -1

    const annoLoop = () => {
      if (this.active && this.annotations.length > 0) {
        const curFrame = videoTimeToFrame(this.controller.getSync(), video.currentTime)
        if (!video.paused || curFrame !== lastAnnoFrame) {
          this.drawAnnotationOverlay()
          lastAnnoFrame = curFrame
        }
        // If any visible annotation is pitch-anchored we need a fresh
        // homography window. Single-flight guard makes this cheap.
        const needsMatrix =
          this.pitchMode || this.annotations.some((a) => a.data && a.data.coord_space === 'pitch')
        if (needsMatrix) this.controller.telestration.requestTelestrationData()
      }
      this.rafId = requestAnimationFrame(annoLoop)
    }
    this.rafId = requestAnimationFrame(annoLoop)
  }

  // -------------------------------------------------------------------------
  // Screenshot + seek
  // -------------------------------------------------------------------------

  captureScreenshot(): void {
    const video = this.video
    if (!video) return

    const w = video.videoWidth || 1920
    const h = video.videoHeight || 1080
    const offscreen = document.createElement('canvas')
    offscreen.width = w
    offscreen.height = h
    const ctx = offscreen.getContext('2d')!

    // Draw video frame
    ctx.drawImage(video, 0, 0, w, h)
    // Draw telestration overlay
    const telC = document.getElementById('telestration-canvas') as HTMLCanvasElement | null
    if (telC && telC.width > 0) ctx.drawImage(telC, 0, 0, w, h)
    // Draw annotation overlay
    if (this.annoCanvas && this.annoCanvas.width > 0) ctx.drawImage(this.annoCanvas, 0, 0, w, h)

    offscreen.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const frame = videoTimeToFrame(this.controller.getSync(), video.currentTime)
      a.download = `${this.controller.gameCode}_frame${frame}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  seekToAnnotation(frame: number): void {
    const video = this.video
    if (!video) return
    video.currentTime = frameToVideoTime(this.controller.getSync(), frame)
  }
}
